import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CONTENT_FORMAT_LABELS, CONTENT_FORMATS, type ContentFormat } from '@/lib/contentFormat';
import type { ViewFormat } from '@/hooks/useContentFormat';

interface ContentFormatSelectProps {
  /** Currently selected view — a detected/overridden `ContentFormat`, or `'raw'` for the unformatted source. */
  value: ViewFormat;
  /** The format `detectContentFormat()` actually picked, shown as "(detected)" next to the matching option. */
  detected: ContentFormat;
  onChange: (format: ViewFormat) => void;
  className?: string;
}

/**
 * A single dropdown covering every way a wiki article's or publication
 * section's content can be viewed: each markup format Wikistar can
 * render (Djot / AsciiDoc / Markdown / Plain text), plus "Raw" to see
 * the exact, unformatted source. Raw used to be a separate toggle button
 * next to this dropdown; consolidating it here avoids showing two
 * controls that both just say "how should this content be displayed."
 */
export function ContentFormatSelect({ value, detected, onChange, className }: ContentFormatSelectProps) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as ViewFormat)}>
      <SelectTrigger
        size="sm"
        className={`h-6 w-auto gap-1 border-transparent px-2 py-0.5 text-[11px] text-muted-foreground shadow-none hover:bg-accent/50 ${className ?? ''}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {CONTENT_FORMATS.map((f) => (
          <SelectItem key={f} value={f} className="text-xs">
            {CONTENT_FORMAT_LABELS[f]}
            {f === detected ? ' (detected)' : ''}
          </SelectItem>
        ))}
        <SelectItem value="raw" className="text-xs">
          Raw (unformatted source)
        </SelectItem>
      </SelectContent>
    </Select>
  );
}
